<script>
/**
 * @component AlertasListaCompacta
 * @description Lista compacta de alertas no críticas para el rediseño del
 *              tablero de seguimiento (UC-REG-065). Densa, una fila por
 *              alerta, escaneable. Usa el modelo mental de "inbox" — el
 *              médico procesa de arriba hacia abajo.
 *
 *              Para las alertas críticas (semáforo rojo + score≥9 o keyword
 *              crítica) usar `AlertaCriticaCard.vue` arriba de esta lista
 *              (decisión visual del componente padre `DashboardAlerts.vue`).
 *
 * @example <AlertasListaCompacta :alerts="alerts" @atender="..." @marcar="..." />
 */
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

export default {
  name: "AlertasListaCompacta",

  props: {
    /**
     * @description Array de alertas. Cada una con shape:
     *              { _id, patientId, patientName, type, taskName,
     *                completedMeasurement, value, score, completedAt }
     */
    alerts: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    typeMap() {
      return {
        "health-conversation": { icon: "💬", label: "Conv. salud" },
        vital: { icon: "🩺", label: "Vital" },
        checkin: { icon: "✓", label: "Check-in" },
        medication: { icon: "💊", label: "Medicación" },
      };
    },
  },

  methods: {
    /**
     * @description "hace 2h", "hace 1d", etc.
     */
    relativeTime(date) {
      if (!date) return "";
      return formatDistanceToNow(new Date(date), {
        locale: es,
        addSuffix: false,
      });
    },
    typeIcon(type) {
      return this.typeMap[type]?.icon || "📋";
    },
    typeLabel(type) {
      return this.typeMap[type]?.label || type;
    },
    /**
     * @description Snippet de info más relevante: para vital = valor, para
     *              healthConversation = score X/10, para checkin/medication =
     *              taskName.
     */
    snippet(alert) {
      if (alert.score != null) return `score ${alert.score}/10`;
      if (alert.value) return alert.value;
      return alert.taskName || "—";
    },
    onAtender(alert) {
      this.$emit("atender", alert);
    },
    onMarcar(alert, e) {
      e.stopPropagation();
      this.$emit("marcar", alert);
    },
  },
};
</script>

<template lang="pug">
  .alertas-lista
    .alertas-lista__empty(v-if="!alerts.length")
      i.el-icon-success
      span Sin alertas pendientes
    .alertas-lista__row(
      v-for="alert in alerts"
      :key="alert._id"
      :class="`alertas-lista__row--${alert.completedMeasurement || 'red'}`"
      @click="onAtender(alert)"
      role="button"
      tabindex="0"
    )
      span.alertas-lista__semaphore(:class="`semaphore--${alert.completedMeasurement || 'red'}`")
      span.alertas-lista__patient {{ alert.patientName }}
      span.alertas-lista__type
        span {{ typeIcon(alert.type) }}
        span {{ typeLabel(alert.type) }}
      span.alertas-lista__snippet {{ snippet(alert) }}
      span.alertas-lista__time hace {{ relativeTime(alert.completedAt) }}
      span.alertas-lista__actions
        el-button(size="mini" type="primary" @click.stop="onAtender(alert)") Atender
        el-button(size="mini" icon="el-icon-check" @click.native="onMarcar(alert, $event)" title="Marcar visto")
</template>

<style lang="scss" scoped>
.alertas-lista {
  display: flex;
  flex-direction: column;
  font-family: "Plus Jakarta Sans", sans-serif;

  &__empty {
    padding: 32px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    i {
      color: #1f8a4c;
      font-size: 16px;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: 16px 1.2fr 1.2fr 1.5fr 80px 180px;
    gap: 12px;
    align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid #f0f2f7;
    cursor: pointer;
    transition: background 0.1s ease;

    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: #fafbfd;
    }

    &--red {
      border-left: 3px solid #b3261e;
      padding-left: 13px;
    }
    &--yellow {
      border-left: 3px solid #b07c0a;
      padding-left: 13px;
    }
    &--green {
      border-left: 3px solid #1f8a4c;
      padding-left: 13px;
    }
  }

  &__semaphore {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    &.semaphore--red {
      background: #b3261e;
    }
    &.semaphore--yellow {
      background: #b07c0a;
    }
    &.semaphore--green {
      background: #1f8a4c;
    }
  }

  &__patient {
    font-weight: 600;
    color: #0f1e36;
    font-size: 13px;
  }

  &__type {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #4b5563;
    font-weight: 500;
  }

  &__snippet {
    font-size: 12px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__time {
    font-size: 11px;
    color: #9ca3af;
    text-align: right;
  }

  &__actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }
}
</style>
